import type * as CSS from 'csstype';
import type React from 'react';
import styled, { css, type FlattenSimpleInterpolation } from 'styled-components';

import Icon from '#@/components/Icon';
import IconClickable, { type IconClickableProps } from '#@/components/IconClickable';
import IconInteractable from '#@/components/IconInteractable';
import { COLOR, mixins, sizes, textStyles } from '#@/shared/utils/styles';

import { baseDisabledTextOverrides, borderlessOverrides } from './commonStyles';

export type InputContentPosition = 'left' | 'right';

export type InputSize = 'small' | 'normal';

export interface LayoutProps {
  icon?: string;
  iconPosition?: InputContentPosition;
  prefixLabel?: React.ReactNode;
  prefixLabelWidth?: number;
  prefixLabelRightOffset?: number;
  suffixLabel?: React.ReactNode;
  suffixLabelWidth?: number;
  $appearanceStyle: FlattenSimpleInterpolation;
}

export const Layout = styled.div<LayoutProps>`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;

  ${(props) => props.$appearanceStyle}

  ${(props) =>
    props.icon &&
    css`
      input {
        padding-${props.iconPosition ?? 'left'}: 38px;
      }
    `};

  ${(props) =>
    Boolean(props.prefixLabel) &&
    props.prefixLabelWidth !== undefined &&
    css`
      input {
        padding-left: ${props.prefixLabelWidth + (props.prefixLabelRightOffset || 0) + 20}px;
      }
    `};

  ${(props) =>
    Boolean(props.suffixLabel) &&
    props.suffixLabelWidth !== undefined &&
    css`
      input {
        padding-right: ${props.suffixLabelWidth + 20}px;
      }
    `};
`;

export interface AppearanceStyleProps {
  invalid?: boolean;
  textAlign?: CSS.Property.TextAlign;
  cursor?: CSS.Property.Cursor;
  borderless?: boolean;
  disabled?: boolean;
  size?: InputSize;
}

export const getDefaultAppearanceStyle = ({
  size,
  borderless,
  cursor,
  invalid,
  textAlign,
}: AppearanceStyleProps) => css`
  height: ${size === 'small' ? sizes.inputHeightSmall : sizes.inputHeight}px;
  color: ${COLOR.textPrimary};

  input {
    width: 100%;
    height: 100%;
    ${textStyles.bodyMedium450}
    padding: 0 ${size === 'small' ? 8 : 12}px;
    border-radius: 8px;
    color: ${COLOR.textPrimary};
    border: 1px solid ${COLOR.borderMedium};
    background: #fff;

    &:disabled {
      background: ${COLOR.brandLightestAlpha};
      border-color: ${COLOR.borderMedium};
      ${baseDisabledTextOverrides}
    }

    &:focus {
      ${!borderless &&
      css`
        border-color: ${COLOR.highlightNeon};
        outline: 2px solid ${mixins.rgba(COLOR.brand, 0.2)};
      `}
    }

    ::placeholder {
      color: ${COLOR.textTertiary};
      opacity: 1;
    }

    cursor: ${cursor};

    &::-webkit-inner-spin-button {
      margin-left: 10px;
    }

    ${invalid &&
    css`
      & {
        border-color: ${COLOR.danger};
      }
      &:focus {
        border-color: ${COLOR.danger};
        outline: 2px solid ${mixins.rgba(COLOR.danger, 0.2)};
      }
    `}

    ${textAlign &&
    css`
      text-align: ${textAlign};
    `}
  }

  ${borderless &&
  css`
    input {
      ${borderlessOverrides}
    }
  `}
`;

const clickableIconWidth = sizes.iconPaddedSize;
const iconMargin = 12;
const positionIcon = css`
  position: absolute;
  transform: translate(0, -50%);
  top: 50%;
`;

interface IconPositionerProps {
  $position?: 'left' | 'right';
}

interface InteractableIconPositionerProps extends IconPositionerProps {
  $iconWidth?: number;
  onClick?: () => void;
}

export const InteractableIconPositioner = styled(IconInteractable)<InteractableIconPositionerProps>`
  ${positionIcon}
  ${({ $position }) => $position ?? 'left'}: ${({ $iconWidth = 18 }) =>
    (iconMargin - (clickableIconWidth - $iconWidth) / 2).toFixed(1)}px;
`;

export const StyledIcon = styled(Icon)<IconPositionerProps>`
  ${positionIcon}
  ${({ $position }) => $position ?? 'left'}: ${iconMargin}px;

  color: ${COLOR.textTertiary};
  cursor: auto;
`;

const defaultCursorIfNoOnClick = css<IconClickableProps>`
  ${({ onClick }) =>
    !onClick &&
    css`
      cursor: default;
    `}
`;

export const StyledIconClickable = styled(IconClickable)`
  color: ${COLOR.textTertiary};
  pointer-events: auto;

  ${defaultCursorIfNoOnClick};
`;
